import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Clock, FileSignature, ChevronRight, CheckCircle2, ListChecks } from "lucide-react";

const SCENES = [
  { caption: "Your clients log in to their branded portal" },
  { caption: "They track every 45 & 180-day deadline" },
  { caption: "…and e-sign documents in a click" },
];
const DURATION = 3400;

const Chrome = ({ children }) => (
  <>
    <div className="flex items-center gap-2 px-4 py-3 bg-[#F4F4F5] border-b border-black/5">
      <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
      <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
      <span className="w-3 h-3 rounded-full bg-[#28C840]" />
      <div className="ml-3 flex-1 max-w-[240px] bg-white border border-black/5 rounded-md text-[11px] text-ally-slate px-3 py-1 truncate">
        portal.yourfirm.com
      </div>
    </div>
    <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-black/5 bg-white">
      <span className="font-display font-black text-base text-ally-ink">Your Firm</span>
      <span className="text-[11px] font-bold text-ally-slate">Sign out</span>
    </div>
    {children}
  </>
);

const sceneAnim = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.5, ease: "easeOut" },
};

// Scene 0 — portal home
const SceneHome = () => (
  <motion.div {...sceneAnim} className="p-4 sm:p-5 bg-[#FBFBFC]">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-ally-pink"><ShieldCheck size={12} /> Your exchange portal</div>
    <div className="font-display text-lg sm:text-xl font-black text-ally-ink mt-1.5">Welcome, Daniel</div>
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
      className="mt-3 flex items-center gap-2.5 rounded-xl bg-ally-pink/10 px-3 py-2.5">
      <FileSignature size={16} className="text-ally-pink shrink-0" />
      <span className="text-[12px] font-bold text-ally-ink">1 document awaiting your signature</span>
    </motion.div>
    <div className="mt-3 space-y-2.5">
      {[
        { name: "Redwood Logistics Center", addr: "1420 Harbor Blvd, Oakland, CA", status: "In escrow", tone: "#2563EB", id: "12d", close: "138d", urgent: false },
        { name: "Bayfront Retail Portfolio", addr: "88 Marina Way, San Diego, CA", status: "Identifying", tone: "#D97706", id: "4d", close: "150d", urgent: true },
      ].map((e, i) => (
        <motion.div key={e.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.2 }}
          className="bg-white border border-black/5 rounded-2xl p-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: e.tone }}>{e.status}</span>
            <div className="font-display text-sm font-bold text-ally-ink mt-1.5 truncate">{e.name}</div>
            <div className="text-[11px] text-ally-slate truncate">{e.addr}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[11px]">
              <span className="flex items-center gap-1 text-ally-slate"><Clock size={11} className={e.urgent ? "text-red-500" : "text-ally-slate"} /> Identify by <b style={{ color: e.urgent ? "#EF4444" : "#FF0083" }}>{e.id} left</b></span>
              <span className="flex items-center gap-1 text-ally-slate"><Clock size={11} className="text-ally-slate" /> Close by <b className="text-ally-pink">{e.close} left</b></span>
            </div>
          </div>
          <ChevronRight size={16} className="text-ally-slate shrink-0" />
        </motion.div>
      ))}
    </div>
  </motion.div>
);

// Scene 1 — exchange detail with deadline + checklist progress
const SceneDetail = () => (
  <motion.div {...sceneAnim} className="p-4 sm:p-5 bg-[#FBFBFC]">
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "#2563EB" }}>In escrow</span>
    <div className="font-display text-lg font-black text-ally-ink mt-2">Redwood Logistics Center</div>
    <div className="text-[11px] text-ally-slate">1420 Harbor Blvd, Oakland, CA</div>
    <div className="grid grid-cols-2 gap-2.5 mt-4">
      {[
        { label: "Identify by", big: "12", unit: "days left", urgent: true },
        { label: "Close by", big: "138", unit: "days left", urgent: false },
      ].map((d) => (
        <div key={d.label} className="bg-white border border-black/5 rounded-xl p-3">
          <div className="text-[10px] text-ally-slate">{d.label}</div>
          <div className="font-display text-2xl font-black leading-none mt-1" style={{ color: d.urgent ? "#EF4444" : "#111114" }}>{d.big}</div>
          <div className="text-[10px] text-ally-slate mt-0.5">{d.unit}</div>
        </div>
      ))}
    </div>
    <div className="bg-white border border-black/5 rounded-xl p-3 mt-3">
      <div className="flex items-center justify-between text-[11px] font-bold text-ally-ink">
        <span className="flex items-center gap-1.5"><ListChecks size={13} className="text-ally-pink" /> Exchange checklist</span>
        <span className="text-ally-slate">6 / 9</span>
      </div>
      <div className="h-2 bg-black/5 rounded-full mt-2 overflow-hidden">
        <motion.div initial={{ width: "0%" }} animate={{ width: "66%" }} transition={{ duration: 1.4, ease: "easeInOut", delay: 0.3 }}
          className="h-full bg-ally-pink rounded-full" />
      </div>
    </div>
  </motion.div>
);

// Scene 2 — e-sign a document
const SceneSign = () => {
  const [signed, setSigned] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSigned(true), 1400); return () => clearTimeout(t); }, []);
  return (
    <motion.div {...sceneAnim} className="p-4 sm:p-5 bg-[#FBFBFC]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-ally-pink"><FileSignature size={12} /> Sign document</div>
      <div className="font-display text-base font-black text-ally-ink mt-1.5">Exchange Agreement</div>
      <div className="bg-white border border-black/5 rounded-xl p-4 mt-3">
        <div className="space-y-2">
          <div className="h-2 bg-black/5 rounded-full w-full" />
          <div className="h-2 bg-black/5 rounded-full w-11/12" />
          <div className="h-2 bg-black/5 rounded-full w-4/5" />
        </div>
        <div className="mt-4 border-t border-dashed border-black/10 pt-3 flex items-center justify-between">
          <span className="text-[11px] text-ally-slate">Client signature</span>
          <span className="font-display text-lg text-ally-ink" style={{ fontStyle: "italic", opacity: signed ? 1 : 0.25 }}>Daniel R.</span>
        </div>
      </div>
      <AnimatePresence mode="wait">
        {!signed ? (
          <motion.div key="btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="mt-3 w-full text-center bg-ally-pink text-white rounded-full py-2.5 text-sm font-bold">
            Click to sign
          </motion.div>
        ) : (
          <motion.div key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-green-50 text-green-700 rounded-full py-2.5 text-sm font-bold">
            <CheckCircle2 size={16} /> Signed just now
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SCENE_COMPONENTS = [SceneHome, SceneDetail, SceneSign];

export function ClientPortalClip() {
  const [scene, setScene] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setScene((s) => (s + 1) % SCENES.length), DURATION);
    return () => clearInterval(t);
  }, []);
  const Active = SCENE_COMPONENTS[scene];

  return (
    <div className="relative w-full max-w-md mx-auto lg:max-w-none" data-testid="client-portal-clip">
      <div className="absolute -inset-6 bg-ally-pink/20 blur-3xl rounded-full opacity-40" aria-hidden />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative bg-white rounded-[22px] shadow-2xl shadow-black/40 border border-black/5 overflow-hidden"
      >
        {/* progress segments */}
        <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 p-2">
          {SCENES.map((_, i) => (
            <div key={i} className="flex-1 h-1 rounded-full bg-black/10 overflow-hidden">
              {i < scene && <div className="h-full w-full bg-ally-pink" />}
              {i === scene && (
                <motion.div key={scene} initial={{ width: "0%" }} animate={{ width: "100%" }}
                  transition={{ duration: DURATION / 1000, ease: "linear" }} className="h-full bg-ally-pink" />
              )}
            </div>
          ))}
        </div>

        <Chrome>
          <div className="min-h-[300px]">
            <AnimatePresence mode="wait">
              <Active key={scene} />
            </AnimatePresence>
          </div>
        </Chrome>
      </motion.div>

      {/* caption */}
      <div className="mt-4 text-center">
        <AnimatePresence mode="wait">
          <motion.p key={scene} data-testid="clip-caption"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="text-white/70 text-sm font-medium inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-ally-pink animate-pulse" /> {SCENES[scene].caption}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
